import { GetPreferencesQueryResponse, useGetPreferences } from './UseGetPreferences';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CategoryLeaderBoardGroupPreference,
  DeletePreferencePermissionRequest,
  DeletePreferencesPermissionMutateOptions,
  FeatureLeaderBoardGroupPreference,
  OpportunityReviewBoardGroupPreference,
  PreferenceResponse,
  PreferenceResponseStatus,
  ProductAnalyzerGroupPreference,
} from '@utils/userPreferencesUtils/Types';
import { PreferenceApiService } from '@services/userPreferences/PreferencesApi.service';

export interface DeletePreferencePermissionMutateResponse<T> {
  data: PreferenceResponse<T> | null;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  mutate: (variables: DeletePreferencePermissionRequest, options?: DeletePreferencesPermissionMutateOptions) => void;
  reset: () => void;
}

/**
 * @return DeletePreferenceMutateResponse Object
 *
 * @remarks
 * Hooks for delete preference api call using useMutate to manage server state
 *
 * @param type       - to difference the different pages (example: "category-leaderboard-group" for Category Leaderboard Page)
 * @param namespace  - combination of retailer client and country
 * @param getLeaderboardGroups
 * **/
export const useDeletePermissionPreferences = <T>(
  type: string,
  namespace: string,
  getLeaderboardGroups:
    | GetPreferencesQueryResponse<CategoryLeaderBoardGroupPreference>
    | GetPreferencesQueryResponse<FeatureLeaderBoardGroupPreference>
    | GetPreferencesQueryResponse<ProductAnalyzerGroupPreference>
    | GetPreferencesQueryResponse<OpportunityReviewBoardGroupPreference>,
): DeletePreferencePermissionMutateResponse<T> => {
  const getPreferences = useGetPreferences(type, namespace);

  const queryClient = useQueryClient();

  const { data, isPending, isSuccess, isError, mutate, reset } = useMutation({
    mutationKey: ['preferences', type, namespace],

    mutationFn: async (request: DeletePreferencePermissionRequest) => {
      const currentPreference = getPreferences.data.find(
        (preference) => preference.namespace === namespace && preference.key === request.key,
      );

      return PreferenceApiService.deletePreferencesPermissions(
        type,
        currentPreference?.id ?? '',
        request.userId,
        currentPreference?.key ?? '',
      );
    },
    onSuccess: (response) => {
      if (response.status === PreferenceResponseStatus.SUCCESS) {
        queryClient.invalidateQueries({ queryKey: ['preferences'] });
      }
      //Force getLeaderboardGroups method refetch after permission delete
      getLeaderboardGroups.refetch();
    },
  });

  return {
    data: data ?? null,
    isLoading: isPending,
    isError,
    isSuccess,
    mutate,
    reset,
  };
};
